import clsx from 'clsx';
import { CashoutIcon } from '@/components/icons/svg-icons';
import { useBetslipContext } from '@/features/betslip/contexts/betslip-context';
import { BetMsgModel, BET_TYPE, TicketModel } from '@/hooks/query';
import { useBookingOddsType } from '@/features/betslip/betslip-booking/hooks/use-booking-odds-type';
import { useSiteStore } from '@/store/site';
import { getAuthToken } from '@/store/auth';
import { ApiQueryPromise } from '@/types';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { getResponseJwtHeader } from '@/utils/web';
import { decimalOdds } from '@/utils/odds-utils';
import { CashOutSportList } from '@nf/types/site';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { idealRoundFixed } from '@nf/utils-common/numbers';
import { useTranslations } from 'next-intl';
import { use, useEffect, useState } from 'react';
import { useBetslipStore } from '@/store/betslip';
import { betslipStyle } from '@/features/betslip/betslip.style';
import { reservationService } from '@/features/betslip/betslip-booking/services/reservation.service';
import { VoucherType } from '@nf/achievement-system-v2';

interface BetTicketOddsGroupProps {
	ticket: TicketModel;
	canBetResult?: BetMsgModel;
	isParlayConfirmBet?: boolean;
}

interface CheckBetTypeRequest {
	matchid: number,
	bettype: number,
	sportType: number
}

interface CheckBetTypeResponse {
	CashOut: boolean
}

export const BetTicketOddsGroup = ({ ticket, canBetResult, isParlayConfirmBet }: BetTicketOddsGroupProps) => {
	const { oddsType, cashOutSportList } = useBetslipContext();

	const [
		isBookingBet,
		useVoucher
	] = useBetslipStore(state => [
		state.isBookingBet,
		state.useVoucher,
	]);
	const scoreBoxBetType = [BET_TYPE.ScoreBoxHdp, BET_TYPE.ScoreBoxOu] as Array<string>;
	const showOdds = !scoreBoxBetType.includes(ticket.Bettype) && ![6, 55].includes(canBetResult?.code ?? 0); // 6: Closed, 55: DisableOutright
	const useOddsBooster = (useVoucher === 'OddsBooster' || useVoucher === 'OddsBooster-promo' || useVoucher === 'AffPromotionOddsBoost');
	const showCashOutIcon = ticket.hasCashOut && ticket.CustomCurrencyName !== 'SBC' && useVoucher == 'None' && !ticket.RewardModel;
	
	return (
		<div className={clsx(
			'ticket__odds-group',
			useOddsBooster ? { [betslipStyle.oddsBoost]: true } : ''        // 有使用oods boost 時加上betslipStyle.oddsBoost 樣式
		)}
		>
			{
				showOdds ? (
					<>
						<Odds
							ticket={ticket}
							oddsType={oddsType}
							useOddsBooster={useOddsBooster}
							isBookingBet={isBookingBet}
						/>

						<OddsType
							ticket={ticket}
							oddsType={oddsType}
							useOddsBooster={useOddsBooster}
							isBookingBet={isBookingBet}
							isParlayConfirmBet={isParlayConfirmBet ?? false}
						/>
					</>
				) : null
			}
			{/* 有使用odds boost 時不支援cash out 需移除*/}
			{showCashOutIcon ? (
				<CashOutIcon
					ticket={ticket}
					cashOutSportList={cashOutSportList}
				/>
			) : null}
		</div>
	);
};

const Odds = ({
	ticket,
	oddsType,
	useOddsBooster,
	isBookingBet
}: {
	ticket: TicketModel,
	oddsType: number,
	useOddsBooster: boolean,
	isBookingBet: boolean,
}) => {
	const [
		betslipTab,
		betslipType,
		isConfirmBet,
	] = useBetslipStore(state => [
		state.betslipTab,
		state.betslipType,
		state.isConfirmBet
	]);
	const { isConstrainedOddsType } = useBookingOddsType(ticket.PriceType);
	const { isIndependentMode } = useBetslipContext();

	let odds = ticket.DisplayOdds;
	if (isBookingBet && !reservationService.fixedDecimalBetTypes.includes(Number(ticket.Bettype))) {
		odds = isConstrainedOddsType
			? odds
			: idealRoundFixed(decimalOdds(ticket.originalPrice), 2);
	}

	let oddsChangeStatus = '';
	if (ticket.isOddsChange) {
		const oldOdds = ticket.PreDisplayOdds;
		if (Number(odds) > Number(oldOdds)) oddsChangeStatus = 'change-up';
		if (Number(odds) < Number(oldOdds)) oddsChangeStatus = 'change-down';
	}

	// 此情境需使用parlay
	if (
		!isBookingBet &&
		isIndependentMode &&
		betslipTab == 'parlay' &&
		betslipType === 'multi' &&
		!isConfirmBet
	) {
		odds = ticket.DisplayParlayOdds;
	}
	
	return (
		<>
			{
				ticket.OddsBeforeOddsBoost && useOddsBooster ? (
					<>
						<div className={clsx('ticket__odds', betslipStyle.odds)} data-status={oddsChangeStatus}>
							<span className={Number(odds) < 0 ? betslipStyle.oddsMinus : betslipStyle.oddsNormal}>{ticket.OddsBeforeOddsBoost}</span>
						</div>
						<div className={betslipStyle.oddsBoostTag}>
							<i className="odds-boost-icon odds-boost-icon--odds-boost" />
							<span>{odds}</span>
						</div>
					</>
				) : (
					//OddsBoostType = 1 promotion oddsboost
					<div className={clsx('ticket__odds', betslipStyle.odds)} data-status={oddsChangeStatus}>
						<span className={Number(odds) < 0 ? betslipStyle.oddsMinus : betslipStyle.oddsNormal}>{isConfirmBet && ticket?.OddsBoostType === 1 ? ticket.PreDisplayOdds : odds}</span>
					</div>
				)
			}
		</>
	)
}

const OddsType = ({ ticket, oddsType, useOddsBooster, isBookingBet, isParlayConfirmBet }: {
	ticket: TicketModel,
	oddsType: number,
	useOddsBooster: boolean,
	isBookingBet: boolean,
	isParlayConfirmBet: boolean
}) => {
	const t = useTranslations('General');
	const [
		betslipTab,
		betslipType
	] = useBetslipStore(state => [
		state.betslipTab,
		state.betslipType
	]);
	const { isIndependentMode } = useBetslipContext();
	const { bookingOddsType } = useBookingOddsType(ticket.PriceType);

	// isParlayConfirmBet 用於固定將 ConfirmBet Parlay 票下的單一 ticket 顯示為 Dec
	let newOddsType = oddsType;
	if (isBookingBet) {
		newOddsType = bookingOddsType;
	} else if (ticket.PriceType === 1 || isParlayConfirmBet || (betslipTab === 'parlay' && isIndependentMode && betslipType === 'multi')) {
		newOddsType = 1;
	} else {
		newOddsType = (ticket.isMMR ? 6 : oddsType);
	}

	const getOddsTypeName = (oddsId: number) => {
		let oddsName = '';
		switch (oddsId) {
			case 2:
				oddsName = t('China_Odds'); // CN
				break;
			case 1:
			case 3:
			case 4:
			case 5:
			case 6:
				oddsName = t('lbl_OddsTypeMin' + oddsId);
				break;
		}
		return oddsName;
	}

	return (
		<div className="ticket__odds-type">
			({getOddsTypeName(newOddsType)})
		</div>
	);
}

const CashOutIcon = ({ ticket, cashOutSportList }: {
	ticket: TicketModel,
	cashOutSportList: CashOutSportList
}) => {
	const [apiDomain] = useSiteStore(state => [state.apiDomain]);
	const [isShowIcon, setIsShowIcon] = useState(false);

	const checkCashOutBetType = async (req: CheckBetTypeRequest): Promise<ApiQueryPromise<CheckBetTypeResponse>> => {
		const formData = new FormData();
		const authToken = getAuthToken();

		for (const [key, value] of Object.entries(req)) {
			formData.append(key, value.toString());
		}

		const options = {
			body: formData
		};

		const response = await fetchComposer.postWithBearerBase(cloudUrlParser(`${apiDomain.ctcdDomain}/CashOut/ChkBettype`), options)(authToken);

		if (!response.ok) {
			throw new Error(`${response.status}: ${response.statusText}`);
		}

		return {
			data: await response.json(),
			jwtToken: getResponseJwtHeader(response)
		};
	};

	useEffect(() => {
		const cashOutType = cashOutSportList[ticket.SportType] ?? [];

		if (cashOutType.includes(ticket.Bettype) && ticket.IsCashoutEnabled) {
			setIsShowIcon(true);
		}
	}, [ticket]);

	return isShowIcon ? (
		<div className="cashout-icon">
			<CashoutIcon />
		</div>
	) : null;
}
